import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { getColor } from "../../lib/categoryColors";
import { cn } from "../../lib/utils";

/**
 * One row in the AdminCategories drag-to-reorder list.
 * Renders the drag handle, color dot + name, color/swatch label, PDF count,
 * and inline Edit / Delete buttons. Visual style intentionally matches the
 * AdminDashboard PDF table.
 */
export function SortableRow({ category, pdfCounts, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const c = getColor(category.color);
  const count = pdfCounts[category.name] || 0;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "grid grid-cols-[auto_1fr_auto] items-center gap-4 px-4 sm:px-5 py-4 bg-white",
        "border-b border-slate-100 last:border-b-0 transition-colors",
        isDragging ? "shadow-lg ring-1 ring-slate-200 z-10 relative bg-white" : "hover:bg-slate-50/60"
      )}
      data-testid={`category-row-${category.id}`}
    >
      <button
        type="button"
        className={cn(
          "h-8 w-8 rounded-md flex items-center justify-center text-slate-300 hover:text-slate-700 hover:bg-slate-100",
          "cursor-grab active:cursor-grabbing touch-none"
        )}
        aria-label="Drag to reorder"
        data-testid={`category-drag-${category.id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <div className="min-w-0 flex items-center gap-3">
        <span
          className={cn("h-2.5 w-2.5 rounded-full shrink-0", c.dot)}
          style={c.dotStyle}
        />
        <div className="min-w-0">
          <div
            className="font-display font-semibold text-slate-900 truncate"
            data-testid={`category-name-${category.id}`}
          >
            {category.name}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            <Badge
              className={cn("rounded-full border-transparent font-medium mr-2", c.bg, c.text)}
              style={c.badgeStyle}
            >
              {category.color}
            </Badge>
            <span>
              {count} PDF{count === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(category)}
          data-testid={`category-edit-${category.id}`}
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(category)}
          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
          data-testid={`category-delete-${category.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" /> Delete
        </Button>
      </div>
    </li>
  );
}
